import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import IconPicker from "../../components/IconPicker";
import CompanyInfoSchemaDesigner from "../../components/CompanyInfoSchemaDesigner";
import {
  Plus, Trash2, Edit2, X, Save, ChevronDown, ChevronRight,
  FileText, Layers, HelpCircle, GripVertical, ArrowUp, ArrowDown
} from "lucide-react";

function QuestionForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial || {
    question_text: "", explanation: "", esrs_reference: "",
    points: 5, category: "general", sub_domain: "dma",
    requires_ghg_table: false, requires_file_upload: false, ghg_scope: [], order: 1,
    answer_options: []
  });

  const addOption = () => {
    const opts = [...(f.answer_options || []), { id: crypto.randomUUID(), name: "", points: 0, recommendation_details: "" }];
    setF({ ...f, answer_options: opts });
  };
  const updateOption = (idx, field, val) => {
    const opts = [...(f.answer_options || [])];
    opts[idx] = { ...opts[idx], [field]: val };
    setF({ ...f, answer_options: opts });
  };
  const removeOption = (idx) => {
    setF({ ...f, answer_options: (f.answer_options || []).filter((_, i) => i !== idx) });
  };

  const inputCls = "w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm text-gray-900 outline-none focus:ring-1 focus:ring-emerald-500";

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
      <div>
        <label className="block text-xs text-gray-500 mb-1">Question Text *</label>
        <textarea rows={2} value={f.question_text} onChange={e => setF({ ...f, question_text: e.target.value })}
          className={`${inputCls} resize-none`} data-testid="question-text-input" />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Explanation</label>
        <textarea rows={2} value={f.explanation} onChange={e => setF({ ...f, explanation: e.target.value })}
          className={`${inputCls} resize-none`} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">ESRS Reference</label>
          <input value={f.esrs_reference} onChange={e => setF({ ...f, esrs_reference: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Max Points</label>
          <input type="number" min={1} value={f.points} onChange={e => setF({ ...f, points: parseInt(e.target.value) || 1 })} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Category</label>
          <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })} className={inputCls}>
            <option value="general">General</option>
            <option value="environmental">Environmental</option>
            <option value="social">Social</option>
            <option value="governance">Governance</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Order</label>
          <input type="number" min={1} value={f.order} onChange={e => setF({ ...f, order: parseInt(e.target.value) || 1 })} className={inputCls} />
        </div>
      </div>
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={f.requires_ghg_table} onChange={e => setF({ ...f, requires_ghg_table: e.target.checked })} className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
          Requires GHG Table
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={f.requires_file_upload || false} onChange={e => setF({ ...f, requires_file_upload: e.target.checked })} className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
          Requires File Upload
        </label>
      </div>

      <div className="border-t border-gray-200 pt-3 mt-2">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-gray-500 font-medium">Answer Options</label>
          <button onClick={addOption} data-testid="add-answer-option-btn" className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">+ Add Option</button>
        </div>
        {(f.answer_options || []).length === 0 && (
          <p className="text-xs text-gray-400 italic">No custom options. Default Yes/Partially/Limited/No will be used.</p>
        )}
        <div className="space-y-2">
          {(f.answer_options || []).map((opt, idx) => (
            <div key={opt.id || idx} className="bg-white border border-gray-200 rounded p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input placeholder="Option name (e.g. Fully Compliant)" value={opt.name}
                  onChange={e => updateOption(idx, "name", e.target.value)}
                  className="flex-1 bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-900 outline-none" />
                <input type="number" placeholder="Pts" value={opt.points}
                  onChange={e => updateOption(idx, "points", parseInt(e.target.value) || 0)}
                  className="w-16 bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-900 outline-none text-center" />
                <button onClick={() => removeOption(idx)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <input placeholder="Recommendation text (shown in results if this option is selected)" value={opt.recommendation_details || ""}
                onChange={e => updateOption(idx, "recommendation_details", e.target.value)}
                className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-900 outline-none placeholder-gray-400" />
              <div className="flex items-center gap-2 pt-1">
                <label className="flex items-center gap-1 text-xs text-gray-500">
                  User Comment:
                  <select value={opt.comment_mode || "none"}
                    onChange={e => updateOption(idx, "comment_mode", e.target.value)}
                    data-testid={`opt-comment-mode-${idx}`}
                    className="bg-white border border-gray-200 rounded px-2 py-1 text-xs text-gray-900 outline-none">
                    <option value="none">None</option>
                    <option value="optional">Optional</option>
                    <option value="required">Required</option>
                  </select>
                </label>
                <label className="flex items-center gap-1 text-xs text-gray-500">
                  User Document:
                  <select value={opt.document_mode || "none"}
                    onChange={e => updateOption(idx, "document_mode", e.target.value)}
                    data-testid={`opt-document-mode-${idx}`}
                    className="bg-white border border-gray-200 rounded px-2 py-1 text-xs text-gray-900 outline-none">
                    <option value="none">None</option>
                    <option value="optional">Optional</option>
                    <option value="required">Required</option>
                  </select>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Admin-uploaded Recommendation Document (shown to user if sub-optimal answer is selected) */}
      <div className="border-t border-gray-200 pt-3 mt-2">
        <label className="block text-xs text-gray-500 font-medium mb-1.5">
          Recommendation Document <span className="text-gray-400 font-normal">(delivered with final report if user picks a sub-optimal answer)</span>
        </label>
        {f.recommendation_file_id ? (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            <FileText className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-800 flex-1">{f.recommendation_file_name || "Recommendation file"}</span>
            <button onClick={() => setF({ ...f, recommendation_file_id: null, recommendation_file_name: null })}
              data-testid="remove-recommendation-file-btn"
              className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded cursor-pointer hover:bg-emerald-100 transition-colors">
            <Plus className="w-4 h-4" />
            <span>Upload Recommendation Document</span>
            <input type="file" className="hidden" data-testid="upload-recommendation-file"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const fd = new FormData();
                fd.append("file", file);
                const API_URL = process.env.REACT_APP_BACKEND_URL;
                const res = await fetch(`${API_URL}/api/upload`, { method: "POST", body: fd });
                if (res.ok) {
                  const data = await res.json();
                  setF({ ...f, recommendation_file_id: data.id, recommendation_file_name: data.filename });
                } else {
                  alert("Upload failed");
                }
              }} />
          </label>
        )}
      </div>

      <div className="flex gap-2 pt-1">
        <button onClick={() => { if (!f.question_text.trim()) return; onSave(f); }} data-testid="save-question-btn"
          className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors">
          <Save className="w-3 h-3" /> Save
        </button>
        <button onClick={onCancel} className="px-4 py-2 rounded text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 transition-colors">Cancel</button>
      </div>
    </div>
  );
}

function MilestoneSection({ milestone, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingMs, setEditingMs] = useState(false);
  const [msForm, setMsForm] = useState({ name: milestone.name, weight: milestone.weight, order: milestone.order });

  const loadQuestions = useCallback(async () => {
    try {
      const data = await api.getQuestionsByMilestone(milestone.id);
      setQuestions(data);
      setLoaded(true);
    } catch (err) { console.error(err); }
  }, [milestone.id]);

  useEffect(() => { if (open && !loaded) loadQuestions(); }, [open, loaded, loadQuestions]);

  const handleAddQuestion = async (formData) => {
    try { await api.createQuestion(milestone.id, formData); setAdding(false); setLoaded(false); loadQuestions(); }
    catch (err) { alert(err.message); }
  };

  const handleUpdateQuestion = async (qId, formData) => {
    try { await api.updateQuestion(qId, formData); setEditing(null); setLoaded(false); loadQuestions(); }
    catch (err) { alert(err.message); }
  };

  const handleDeleteQuestion = async (qId) => {
    if (!window.confirm("Delete this question?")) return;
    try { await api.deleteQuestion(qId); setQuestions(questions.filter(q => q.id !== qId)); }
    catch (err) { alert(err.message); }
  };

  const handleSaveMilestone = async () => {
    try { await onUpdate(milestone.id, msForm); setEditingMs(false); }
    catch (err) { alert(err.message); }
  };

  // Palette map for the category badge next to each milestone name
  const catColors = {
    general: { bg: "bg-emerald-50", text: "text-emerald-700", icon: "text-emerald-500" },
    environmental: { bg: "bg-green-50", text: "text-green-700", icon: "text-green-500" },
    social: { bg: "bg-blue-50", text: "text-blue-700", icon: "text-blue-500" },
    governance: { bg: "bg-purple-50", text: "text-purple-700", icon: "text-purple-500" },
  };
  // Free-text subdomains fall back to a neutral palette.
  const cc = catColors[(milestone.subdomain || "").toLowerCase()] || { bg: "bg-gray-100", text: "text-gray-700", icon: "text-gray-500" };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`milestone-${milestone.id}`}>
      <div className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        <Layers className={`w-5 h-5 ${cc.icon}`} />
        <div className="flex-1 min-w-0">
          {editingMs ? (
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <input value={msForm.name} onChange={e => setMsForm({ ...msForm, name: e.target.value })}
                className="bg-white border border-gray-300 rounded px-2 py-1 text-sm text-gray-900 outline-none w-48" />
              <input type="number" value={msForm.weight} onChange={e => setMsForm({ ...msForm, weight: parseInt(e.target.value) || 0 })}
                className="bg-white border border-gray-300 rounded px-2 py-1 text-sm text-gray-900 outline-none w-20" placeholder="Weight" />
              <input type="number" value={msForm.order} onChange={e => setMsForm({ ...msForm, order: parseInt(e.target.value) || 1 })}
                className="bg-white border border-gray-300 rounded px-2 py-1 text-sm text-gray-900 outline-none w-16" placeholder="Order" />
              <button onClick={handleSaveMilestone} className="text-emerald-600 hover:text-emerald-700"><Save className="w-4 h-4" /></button>
              <button onClick={() => setEditingMs(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-gray-900 font-medium">{milestone.name}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${cc.bg} ${cc.text}`}>{milestone.subdomain}</span>
              <span className="text-xs text-gray-400">Weight: {milestone.weight}%</span>
              <span className="text-xs text-gray-400">Order: {milestone.order}</span>
            </div>
          )}
        </div>
        <span className="text-xs text-gray-400 mr-2">{loaded ? `${questions.length} questions` : ""}</span>
        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          <button onClick={() => setEditingMs(true)} className="p-1.5 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100" data-testid={`edit-milestone-${milestone.id}`}>
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(milestone.id)} className="p-1.5 text-red-400 hover:text-red-600 rounded hover:bg-red-50" data-testid={`delete-milestone-${milestone.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-200 px-5 py-4 space-y-3">
          {questions.map(q => (
            editing === q.id ? (
              <QuestionForm key={q.id} initial={q} onSave={(data) => handleUpdateQuestion(q.id, data)} onCancel={() => setEditing(null)} />
            ) : (
              <div key={q.id} data-testid={`question-row-${q.id}`}
                className="flex items-start gap-3 bg-gray-50 rounded-lg p-3 group hover:bg-gray-100 transition-colors">
                <GripVertical className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" />
                <HelpCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{q.question_text}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-xs text-gray-400">{q.esrs_reference}</span>
                    <span className="text-xs text-emerald-600 font-medium">{q.points} pts</span>
                    <span className="text-xs text-gray-400">#{q.order}</span>
                    {q.requires_ghg_table && <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">GHG</span>}
                    {q.requires_file_upload && <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">File</span>}
                    {(q.answer_options || []).length > 0 && (
                      <span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">{q.answer_options.length} options</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setEditing(q.id)} className="p-1 text-gray-400 hover:text-gray-700"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleDeleteQuestion(q.id)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            )
          ))}

          {questions.length === 0 && !adding && (
            <p className="text-sm text-gray-400 text-center py-3">No questions in this milestone</p>
          )}

          {adding ? (
            <QuestionForm onSave={handleAddQuestion} onCancel={() => setAdding(false)} />
          ) : (
            <button onClick={() => setAdding(true)} data-testid={`add-question-to-${milestone.id}`}
              className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 transition-colors py-1 font-medium">
              <Plus className="w-4 h-4" /> Add Question
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminAssessments() {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [showCreateAssessment, setShowCreateAssessment] = useState(false);
  const [showCreateMilestone, setShowCreateMilestone] = useState(false);
  const [assessmentForm, setAssessmentForm] = useState({
    name: "", description: "", version: "1.0", is_active: true,
    landing_description: "", landing_features: "",
    icon: "Leaf", color: "emerald", duration_estimate: "",
    price_eur: "", price_mkd: "",
  });
  const [milestoneForm, setMilestoneForm] = useState({ name: "", subdomain: "", weight: 25, order: 1 });
  const [editingLanding, setEditingLanding] = useState(false);
  const [landingForm, setLandingForm] = useState({});

  const loadAssessments = async () => {
    setLoading(true);
    try {
      const data = await api.getAdminAssessments();
      setAssessments(data);
      if (data.length > 0 && !selectedId) setSelectedId(data[0].id);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadMilestones = useCallback(async () => {
    if (!selectedId) return;
    try { const data = await api.getMilestones(selectedId); setMilestones(data); }
    catch (err) { console.error(err); }
  }, [selectedId]);

  useEffect(() => { loadAssessments(); }, []);
  useEffect(() => { loadMilestones(); }, [selectedId, loadMilestones]);

  const handleCreateAssessment = async () => {
    if (!assessmentForm.name.trim()) return;
    try {
      const payload = {
        ...assessmentForm,
        landing_features: assessmentForm.landing_features
          ? assessmentForm.landing_features.split('\n').map(s => s.trim()).filter(Boolean)
          : [],
        price_eur: assessmentForm.price_eur === "" ? null : Number(assessmentForm.price_eur),
        price_mkd: assessmentForm.price_mkd === "" ? null : Number(assessmentForm.price_mkd),
      };
      await api.createAssessment(payload);
      setShowCreateAssessment(false);
      setAssessmentForm({
        name: "", description: "", version: "1.0", is_active: true,
        landing_description: "", landing_features: "",
        icon: "Leaf", color: "emerald", duration_estimate: "",
        price_eur: "", price_mkd: "",
      });
      loadAssessments();
    } catch (err) { alert(err.message); }
  };

  const openLandingEditor = () => {
    if (!selected) return;
    const override = selected.pdf_template_override || {};
    setLandingForm({
      name: selected.name || "",
      landing_description: selected.landing_description || "",
      landing_features: (selected.landing_features || []).join('\n'),
      icon: selected.icon || "Leaf",
      color: selected.color || "emerald",
      duration_estimate: selected.duration_estimate || "",
      price_eur: selected.price_eur ?? "",
      price_mkd: selected.price_mkd ?? "",
      description: selected.description || "",
      is_active: selected.is_active !== false,
      // PDF override fields (flattened for simple editing)
      pdf_title_line1: override.pdf_title_line1 || "",
      pdf_title_line2: override.pdf_title_line2 || "",
      pdf_subtitle: override.pdf_subtitle || "",
      // Per-assessment company info schema
      company_info_schema: selected.company_info_schema || { builtin: {}, custom_fields: [] },
    });
    setEditingLanding(true);
  };

  const handleSaveLanding = async () => {
    if (!selectedId) return;
    try {
      // Separate PDF override fields out of landingForm so we can nest them.
      const { pdf_title_line1, pdf_title_line2, pdf_subtitle, company_info_schema, ...rest } = landingForm;
      const pdf_template_override = {};
      if (pdf_title_line1) pdf_template_override.pdf_title_line1 = pdf_title_line1;
      if (pdf_title_line2) pdf_template_override.pdf_title_line2 = pdf_title_line2;
      if (pdf_subtitle) pdf_template_override.pdf_subtitle = pdf_subtitle;

      const payload = {
        ...rest,
        landing_features: rest.landing_features
          ? rest.landing_features.split('\n').map(s => s.trim()).filter(Boolean)
          : [],
        price_eur: rest.price_eur === "" ? null : Number(rest.price_eur),
        price_mkd: rest.price_mkd === "" ? null : Number(rest.price_mkd),
        pdf_template_override: Object.keys(pdf_template_override).length > 0 ? pdf_template_override : null,
        company_info_schema: company_info_schema || null,
      };
      await api.updateAssessment(selectedId, payload);
      setEditingLanding(false);
      loadAssessments();
    } catch (err) { alert(err.message); }
  };

  const handleDeleteAssessment = async (id) => {
    if (!window.confirm("Delete this assessment and all its milestones/questions?")) return;
    try { await api.deleteAssessment(id); if (selectedId === id) setSelectedId(null); loadAssessments(); }
    catch (err) { alert(err.message); }
  };

  // Reorder assessments — moves the assessment up or down in the landing-page order.
  // Each list item carries an `order` integer; we just swap with the neighbour.
  const handleReorderAssessment = async (id, direction) => {
    const sorted = [...assessments].sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
    const idx = sorted.findIndex(a => a.id === id);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const me = sorted[idx];
    const other = sorted[swapIdx];
    // Normalise — make sure both have an order. If undefined, use their position.
    const myOrder = me.order ?? idx + 1;
    const otherOrder = other.order ?? swapIdx + 1;
    try {
      await Promise.all([
        api.updateAssessment(me.id, { order: otherOrder }),
        api.updateAssessment(other.id, { order: myOrder }),
      ]);
      await loadAssessments();
    } catch (err) { alert(err.message); }
  };

  const handleCreateMilestone = async () => {
    if (!milestoneForm.name.trim() || !selectedId) return;
    try {
      await api.createMilestone(selectedId, milestoneForm);
      setShowCreateMilestone(false);
      setMilestoneForm({ name: "", subdomain: "general", weight: 25, order: milestones.length + 1 });
      loadMilestones();
    } catch (err) { alert(err.message); }
  };

  const handleUpdateMilestone = async (id, data) => { await api.updateMilestone(id, data); loadMilestones(); };
  const handleDeleteMilestone = async (id) => {
    if (!window.confirm("Delete this milestone and all its questions?")) return;
    try { await api.deleteMilestone(id); loadMilestones(); } catch (err) { alert(err.message); }
  };

  const selected = assessments.find(a => a.id === selectedId);
  const inputCls = "w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-emerald-500";

  return (
    <div data-testid="admin-assessments-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Assessment Builder</h1>
        <button onClick={() => setShowCreateAssessment(true)} data-testid="create-assessment-btn"
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> New Assessment
        </button>
      </div>

      {showCreateAssessment && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50" onClick={() => setShowCreateAssessment(false)}>
          <div className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">New Assessment</h2>
              <button onClick={() => setShowCreateAssessment(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              <input data-testid="assessment-name-input" placeholder="Assessment Name" value={assessmentForm.name}
                onChange={e => setAssessmentForm({ ...assessmentForm, name: e.target.value })} className={inputCls} />
              <textarea placeholder="Short Description" value={assessmentForm.description}
                onChange={e => setAssessmentForm({ ...assessmentForm, description: e.target.value })}
                className={`${inputCls} resize-none`} rows={2} />
              <textarea placeholder="Landing Page Description (long-form text for the assessment card)"
                value={assessmentForm.landing_description}
                onChange={e => setAssessmentForm({ ...assessmentForm, landing_description: e.target.value })}
                className={`${inputCls} resize-none`} rows={3} />
              <textarea placeholder="Landing Page Feature Bullets (one per line)"
                value={assessmentForm.landing_features}
                onChange={e => setAssessmentForm({ ...assessmentForm, landing_features: e.target.value })}
                className={`${inputCls} resize-none text-sm`} rows={3} data-testid="assessment-features-input" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Icon</label>
                  <IconPicker value={assessmentForm.icon}
                    onChange={v => setAssessmentForm({ ...assessmentForm, icon: v })}
                    testIdPrefix="create-assessment-icon" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Card Color</label>
                  <select value={assessmentForm.color}
                    onChange={e => setAssessmentForm({ ...assessmentForm, color: e.target.value })} className={inputCls}>
                    <option value="emerald">Emerald</option>
                    <option value="blue">Blue</option>
                    <option value="purple">Purple</option>
                    <option value="amber">Amber</option>
                    <option value="rose">Rose</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input placeholder="Duration (e.g. ~15 min)" value={assessmentForm.duration_estimate}
                  onChange={e => setAssessmentForm({ ...assessmentForm, duration_estimate: e.target.value })} className={inputCls} />
                <input type="number" placeholder="Price EUR" value={assessmentForm.price_eur}
                  onChange={e => setAssessmentForm({ ...assessmentForm, price_eur: e.target.value })}
                  className={inputCls} data-testid="assessment-price-eur-input" />
                <input type="number" placeholder="Price MKD" value={assessmentForm.price_mkd}
                  onChange={e => setAssessmentForm({ ...assessmentForm, price_mkd: e.target.value })} className={inputCls} />
              </div>
              <input placeholder="Version (e.g. 1.0)" value={assessmentForm.version}
                onChange={e => setAssessmentForm({ ...assessmentForm, version: e.target.value })} className={inputCls} />
              <button onClick={handleCreateAssessment} data-testid="submit-assessment-btn"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg transition-colors">Create Assessment</button>
            </div>
          </div>
        </div>
      )}

      {showCreateMilestone && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50" onClick={() => setShowCreateMilestone(false)}>
          <div className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">New Milestone</h2>
              <button onClick={() => setShowCreateMilestone(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Milestone Name" value={milestoneForm.name}
                onChange={e => setMilestoneForm({ ...milestoneForm, name: e.target.value })}
                className={inputCls} data-testid="milestone-name-input" />
              <div>
                <label className="block text-xs text-gray-500 mb-1">Sub-domain / Category (free text — e.g. ICT Risk, Environmental, Governance)</label>
                <input placeholder="e.g. ict-risk" value={milestoneForm.subdomain}
                  onChange={e => setMilestoneForm({ ...milestoneForm, subdomain: e.target.value })}
                  className={inputCls} data-testid="milestone-subdomain-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Weight (%)</label>
                  <input type="number" min={0} max={100} value={milestoneForm.weight}
                    onChange={e => setMilestoneForm({ ...milestoneForm, weight: parseInt(e.target.value) || 0 })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Order</label>
                  <input type="number" min={1} value={milestoneForm.order}
                    onChange={e => setMilestoneForm({ ...milestoneForm, order: parseInt(e.target.value) || 1 })} className={inputCls} />
                </div>
              </div>
              <button onClick={handleCreateMilestone} data-testid="submit-milestone-btn"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg transition-colors">Create Milestone</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="text-gray-400">Loading...</div> : (
        <div className="flex gap-6">
          <div className="w-72 flex-shrink-0 space-y-2">
            {(() => {
              const sorted = [...assessments].sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
              return sorted.map((a, idx) => (
              <div key={a.id}
                onClick={() => setSelectedId(a.id)}
                className={`flex items-center gap-2 px-3 py-3 rounded-xl cursor-pointer transition-colors group ${selectedId === a.id ? "bg-emerald-50 border border-emerald-200" : "bg-white border border-gray-200 hover:bg-gray-50"}`}
                data-testid={`assessment-card-${a.id}`}>
                <FileText className={`w-5 h-5 flex-shrink-0 ${selectedId === a.id ? "text-emerald-600" : "text-gray-400"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{a.name}</p>
                  <p className="text-xs text-gray-400">v{a.version} · #{a.order ?? "—"}</p>
                </div>
                <div className="flex flex-col gap-0.5" onClick={e => e.stopPropagation()}>
                  <button onClick={() => handleReorderAssessment(a.id, "up")}
                    disabled={idx === 0}
                    title="Move up"
                    data-testid={`move-assessment-up-${a.id}`}
                    className="p-0.5 text-gray-400 hover:text-emerald-600 disabled:opacity-30 disabled:hover:text-gray-400">
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleReorderAssessment(a.id, "down")}
                    disabled={idx === sorted.length - 1}
                    title="Move down"
                    data-testid={`move-assessment-down-${a.id}`}
                    className="p-0.5 text-gray-400 hover:text-emerald-600 disabled:opacity-30 disabled:hover:text-gray-400">
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <button onClick={e => { e.stopPropagation(); handleDeleteAssessment(a.id); }}
                  className="p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100"
                  data-testid={`delete-assessment-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              ));
            })()}
            {assessments.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No assessments yet</p>}
          </div>

          <div className="flex-1 min-w-0">
            {selected ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{selected.name}</h2>
                    <p className="text-sm text-gray-500">{selected.description || "No description"}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                      {selected.price_eur != null && <span>€{Number(selected.price_eur).toLocaleString()}</span>}
                      {selected.duration_estimate && <span>{selected.duration_estimate}</span>}
                      <span>{selected.is_active === false ? 'Inactive' : 'Active'}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={openLandingEditor} data-testid="edit-landing-btn"
                      className="flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                      <Edit2 className="w-4 h-4" /> Landing & Pricing
                    </button>
                    <button onClick={() => { setMilestoneForm({ ...milestoneForm, order: milestones.length + 1 }); setShowCreateMilestone(true); }}
                      data-testid="add-milestone-btn"
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                      <Plus className="w-4 h-4" /> Add Milestone
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  {milestones.map(m => (
                    <MilestoneSection key={m.id} milestone={m} onUpdate={handleUpdateMilestone} onDelete={handleDeleteMilestone} />
                  ))}
                  {milestones.length === 0 && (
                    <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
                      <Layers className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">No milestones yet</p>
                      <p className="text-gray-400 text-sm mt-1">Add milestones to organize your assessment questions</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Select an assessment or create a new one</p>
              </div>
            )}
          </div>
        </div>
      )}

      {editingLanding && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4" onClick={() => setEditingLanding(false)}>
          <div className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-2xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Landing & Pricing — {selected?.name}</h2>
              <button onClick={() => setEditingLanding(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Assessment Name</label>
                <input value={landingForm.name || ""}
                  onChange={e => setLandingForm({ ...landingForm, name: e.target.value })}
                  className={inputCls} data-testid="landing-name-input" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Short Description</label>
                <textarea value={landingForm.description || ""}
                  onChange={e => setLandingForm({ ...landingForm, description: e.target.value })}
                  className={`${inputCls} resize-none`} rows={2} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Landing Page Description</label>
                <textarea value={landingForm.landing_description || ""}
                  onChange={e => setLandingForm({ ...landingForm, landing_description: e.target.value })}
                  className={`${inputCls} resize-none`} rows={3} data-testid="landing-description-input" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Feature Bullets (one per line)</label>
                <textarea value={landingForm.landing_features || ""}
                  onChange={e => setLandingForm({ ...landingForm, landing_features: e.target.value })}
                  className={`${inputCls} resize-none text-sm`} rows={4} data-testid="landing-features-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Icon (lucide-react)</label>
                  <IconPicker value={landingForm.icon}
                    onChange={v => setLandingForm({ ...landingForm, icon: v })}
                    testIdPrefix="landing-icon" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Card Color</label>
                  <select value={landingForm.color || "emerald"}
                    onChange={e => setLandingForm({ ...landingForm, color: e.target.value })} className={inputCls}>
                    <option value="emerald">Emerald</option>
                    <option value="blue">Blue</option>
                    <option value="purple">Purple</option>
                    <option value="amber">Amber</option>
                    <option value="rose">Rose</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Duration</label>
                  <input value={landingForm.duration_estimate || ""}
                    onChange={e => setLandingForm({ ...landingForm, duration_estimate: e.target.value })}
                    placeholder="~15 min" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Price EUR</label>
                  <input type="number" value={landingForm.price_eur ?? ""}
                    onChange={e => setLandingForm({ ...landingForm, price_eur: e.target.value })}
                    className={inputCls} data-testid="landing-price-eur-input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Price MKD</label>
                  <input type="number" value={landingForm.price_mkd ?? ""}
                    onChange={e => setLandingForm({ ...landingForm, price_mkd: e.target.value })} className={inputCls} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={landingForm.is_active !== false}
                  onChange={e => setLandingForm({ ...landingForm, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                Visible on public landing page
              </label>

              {/* PDF title customization (per-assessment) */}
              <div className="border-t border-gray-200 pt-4 mt-2">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">PDF Report Cover</h3>
                <p className="text-xs text-gray-400 mb-3">
                  Override the default title text that appears on this assessment's PDF report cover page.
                  Leave blank to use the global default ("ESG Sustainability / Assessment Report").
                </p>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Title Line 1</label>
                    <input value={landingForm.pdf_title_line1 || ""}
                      onChange={e => setLandingForm({ ...landingForm, pdf_title_line1: e.target.value })}
                      placeholder="e.g. DORA Compliance"
                      className={inputCls} data-testid="pdf-title-line1-input" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Title Line 2</label>
                    <input value={landingForm.pdf_title_line2 || ""}
                      onChange={e => setLandingForm({ ...landingForm, pdf_title_line2: e.target.value })}
                      placeholder="e.g. Readiness Report"
                      className={inputCls} data-testid="pdf-title-line2-input" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Subtitle (optional)</label>
                    <input value={landingForm.pdf_subtitle || ""}
                      onChange={e => setLandingForm({ ...landingForm, pdf_subtitle: e.target.value })}
                      placeholder="e.g. Digital Operational Resilience Act Assessment"
                      className={inputCls} data-testid="pdf-subtitle-input" />
                  </div>
                </div>
              </div>

              {/* Company Information Form designer (per-assessment) */}
              <div className="border-t border-gray-200 pt-4 mt-2">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Company Information Form</h3>
                <p className="text-xs text-gray-400 mb-3">
                  Configure which fields users see on the Company Info step of this assessment.
                  Add custom fields like consents, dropdowns, or file uploads. Non-consent fields
                  are auto-saved on the user profile so they pre-fill on future assessments.
                </p>
                <CompanyInfoSchemaDesigner
                  value={landingForm.company_info_schema || { builtin: {}, custom_fields: [] }}
                  onChange={(s) => setLandingForm({ ...landingForm, company_info_schema: s })} />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={handleSaveLanding} data-testid="save-landing-btn"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg transition-colors">
                  Save Changes
                </button>
                <button onClick={() => setEditingLanding(false)}
                  className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
